import React from 'react';

import { getLunaSampleUrl } from '@vlabs/api/axios/lp5-client';
import { useGetFaceAttributeSamples } from '@vlabs/api/luna-api/client';
import type { TopMatchFace } from '@vlabs/api/luna-api/model';
import { InfoCircleIcon, DetectionIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { routes } from '@vlabs/shared/navigation/routes';
import { calcTagType } from '@vlabs/shared/utils';
import {
  FaceSample, Tooltip, useWindowSize,
} from '@vlabs/uikit';
import { BaseSamplePlaceholder } from '@vlabs/uikit/components/sample/base-sample/BaseSamplePlaceholder';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';
import { generatePath } from 'react-router-dom';

import { GoToFaceDetailsCircleAnimatedButton } from '@vlabs/monokit/features/go-to-face-details';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { EmptyCard } from '@vlabs/monokit/shared/ui/empty-card';
import { Link } from '@vlabs/monokit/shared/ui/link';

import { useTopMatchFaceCellTranslation } from './i18n';
import st from './TopMatchFaceCell.module.sass';

interface TopMatchFaceCellProps extends WithClassNameProp, WithDataTestid {
  topMatch?: TopMatchFace;
}

const displayName = 'TopMatchFaceCell';
const testid = buildTestid(displayName);

// FIXME Добавить права на просмотр сэмпла из GUI-3600 (http://git.visionlabs.ru/clementine/lerna/-/merge_requests/1228)
function TopMatchFaceCell({
  topMatch = undefined,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: TopMatchFaceCellProps): React.JSX.Element {
  const { t } = useTopMatchFaceCellTranslation();

  const {
    face_id: faceId,
    similarity,
    external_id: externalId,
  } = topMatch ?? {};

  const { data: faceAttributeSamples } = useGetFaceAttributeSamples(faceId ?? '');
  const sampleId = faceAttributeSamples?.samples?.[0];
  const faceSampleUrl = getLunaSampleUrl('faces', sampleId);

  const similarityKind = calcTagType(similarity);

  const { width } = useWindowSize();
  const isSmallScreen = width && width < 1920;

  if (!topMatch) {
    return (
      <EmptyCard
        className={st.EmptyCard}
        dataTip={t('tooltip', { context: 'empty_card' }) as unknown as string}
        text={t('no_data')}
        {...testid(dataTestId)}
      />
    );
  }

  return (
    <div
      className={cn(
        st.Root,
        similarityKind && st[`Root_${similarityKind}`],
        className,
      )}
      {...testid(dataTestId)}
    >
      <FaceSample
        buttonOnTop={faceId ? (
          <GoToFaceDetailsCircleAnimatedButton
            faceId={faceId}
            size="small"
            {...testid(dataTestId)}
          />
        ) : undefined}
        className={st.Sample}
        placeholder={(
          <BaseSamplePlaceholder
            placeholderType="image"
            tooltip={(
              <Tooltip
                clickable
                tooltipContent={t('tooltip', { context: 'empty_sample' })}
              >
                <InfoCircleIcon className={st.TooltipIcon} />
              </Tooltip>
              )}
          />
        )}
        size={isSmallScreen ? 'medium' : 'large'}
        src={faceSampleUrl}
      />
      <div className={st.Info}>
        <SimilarityTag
          icon={<DetectionIcon />}
          size={isSmallScreen ? 'medium' : 'large'}
          title={t('tag_face')}
          value={similarity}
        />
        {!!externalId && (
          <div className={st.InfoRow}>
            <span className={st.Title}>
              {t('external_id')}
            </span>
            <span className={st.Value}>
              <Link
                size="medium"
                to={generatePath(routes?.externalId?.read, { externalId })}
              >
                <TruncatedText
                  clickable
                  hasTruncation
                  tooltipContent={externalId}
                >
                  {externalId}
                </TruncatedText>
              </Link>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

TopMatchFaceCell.displayName = displayName;

export { TopMatchFaceCell };
